import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import API from "../lib/api";
import { isTimeoutError, withTimeout } from "../lib/api";
import {
  Search, ArrowRight, Loader2, AlertTriangle, CheckCircle,
  ShieldCheck, Activity, RadioTower, Database, Network,
  Sparkles, FileText, AlertCircle, Cpu, Zap
} from "lucide-react";
import { PageLayout } from "../components/PageLayout";
import EvidenceGraphPanel from "../components/EvidenceGraphPanel";
import Phase7TeamMode from "./Phase7TeamMode";

const CONTROL_JOB_POLL_INTERVAL_MS = 1500;
const CONTROL_JOB_MAX_DURATION_MS = 45 * 60 * 1000;
const CONTROL_JOB_PENDING_WARNING_MS = 25000; // grace period before showing queue message

function renderEvaluationStartError(error) {
  if (isTimeoutError(error)) {
    return "Control evaluation timed out while starting.";
  }

  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (detail && typeof detail === "object") {
    return detail.message || detail.error || "Control evaluation failed to start.";
  }

  return error?.response?.data?.message || error?.message || "Control evaluation failed to start.";
}

function toSafeControlError(message) {
  const text = String(message || "").toLowerCase();
  if (text.includes("insufficient")) return "Insufficient evidence to validate this control.";
  if (text.includes("only one sql statement") || text.includes("query")) return "Control logic format was invalid; retry evaluation.";
  if (text.includes("timed out")) return "Evaluation timed out for this control.";
  return "Control evaluation failed.";
}

function coerceEvaluationSummary(summary) {
  if (!summary || typeof summary !== "object") return null;
  return {
    persisted_findings: Number(summary.persisted_findings || 0),
    total_in_scope_controls: Number(summary.total_in_scope_controls || 0),
    sql_candidate_controls: Number(summary.sql_candidate_controls || 0),
    llm_candidate_controls: Number(summary.llm_candidate_controls || 0),
    llm_evaluated_controls: Number(summary.llm_evaluated_controls || 0),
    not_testable_controls: Number(summary.not_testable_controls || 0),
    insufficient_data_controls: Number(summary.insufficient_data_controls || 0),
    llm_human_review_controls: Number(summary.llm_human_review_controls || 0),
    successful_controls: Number(summary.successful_controls || 0),
    failed_controls: Array.isArray(summary.failed_controls) ? summary.failed_controls : [],
  };
}

function SamplingCompletePopup() {
  return (
    <div className="sampling-popup-backdrop fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
      <div className="sampling-popup w-full max-w-sm overflow-hidden rounded-3xl border border-emerald-400/40 bg-white shadow-2xl dark:border-emerald-500/30 dark:bg-slate-900">
        <div className="relative bg-gradient-to-br from-emerald-500 to-teal-700 px-6 pb-6 pt-8 text-center text-white">
          <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white/20 ring-4 ring-white/30">
            <CheckCircle className="h-8 w-8 text-white" />
          </div>
          <h3 className="relative mt-4 text-3xl font-black tracking-tight">100%</h3>
          <p className="relative mt-1 text-xs font-bold uppercase tracking-widest text-emerald-100">Sampling Scope Verified</p>
        </div>
        <div className="space-y-4 px-6 py-5">
          <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300 text-center">
            Every uploaded artifact has been mapped into the evaluation matrix. Proceeding to evidence testing.
          </p>
          <div className="flex items-center justify-between rounded-xl bg-emerald-50 px-4 py-3 dark:bg-emerald-950/40">
            <div className="flex items-center gap-2 text-xs font-semibold text-emerald-800 dark:text-emerald-300">
              <RadioTower className="h-4 w-4" />
              Evidence Evaluation Ready
            </div>
            <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400">100% Coverage</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Phase7EvidenceControls() {
  const navigate = useNavigate();
  const location = useLocation();
  const { auditRun, controls = [], findings = [], fetchControls, fetchFindings, setGraphCache } = useAudit();
  const [evaluating, setEvaluating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [jobId, setJobId] = useState(null);
  const [lastEvalSummary, setLastEvalSummary] = useState(null);
  const [latestEvalSummary, setLatestEvalSummary] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [progressLabel, setProgressLabel] = useState("Preparing control evaluation...");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("matrix");
  const [showSamplingPopup, setShowSamplingPopup] = useState(
    Boolean(location.state?.samplingComplete)
  );

  useEffect(() => {
    if (!showSamplingPopup) return;
    const timer = setTimeout(() => setShowSamplingPopup(false), 1500);
    return () => clearTimeout(timer);
  }, [showSamplingPopup]);

  const isTeamMode = auditRun?.audit_mode === "team";

  const fetchTeamData = useCallback(async () => {
    if (!auditRun?.id || !isTeamMode) return;
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/team`);
      setTeamData(data);
    } catch (error) {
      console.error("Failed to fetch team data:", error);
    }
  }, [auditRun?.id, isTeamMode]);

  const fetchLatestEvaluationSummary = useCallback(async () => {
    if (!auditRun?.id || isTeamMode) return;
    try {
      const { data } = await API.get("/audit-logs", {
        params: { run_id: auditRun.id, limit: 100 },
      });
      const logs = Array.isArray(data) ? data : [];
      const latest = logs.find((item) => item?.action === "sql_controls_evaluated");
      setLatestEvalSummary(coerceEvaluationSummary(latest?.details));
    } catch (error) {
      console.error("Failed to fetch latest evaluation summary:", error);
      setLatestEvalSummary(null);
    }
  }, [auditRun?.id, isTeamMode]);

  useEffect(() => {
    if (controls.length === 0) fetchControls();
    if (findings.length === 0) fetchFindings();
    fetchTeamData();
    fetchLatestEvaluationSummary();
  }, [controls.length, findings.length, fetchControls, fetchFindings, fetchTeamData, fetchLatestEvaluationSummary]);

  const handleEvaluate = async () => {
    if (!auditRun?.id) {
      toast.error("Audit run is not available.");
      return;
    }

    try {
      setEvaluating(true);
      setProgress(5);
      setProgressLabel("Preparing control evaluation...");

      const { data } = await API.post(
        `/audit-runs/${auditRun.id}/controls/evaluate-all`,
        {
          artifact_ids: [],
          explain_result: true,
        },
        withTimeout()
      );
      setLastEvalSummary(null);

      const isCompleted = data?.status === "completed" || data?.status === "ok";
      if (!data?.job_id && !isCompleted) {
        setEvaluating(false);
        setProgress(0);
        setProgressLabel("Evaluation failed to start.");
        toast.error(data?.message || "Failed to start control evaluation.");
        return;
      }
      
      if (isCompleted && !data?.job_id) {
        setProgress(100);
        setProgressLabel("Evaluation complete.");
        setEvaluating(false);
        await Promise.all([fetchFindings(), fetchLatestEvaluationSummary(), fetchControls()]);
        if (setGraphCache) {
          setGraphCache({});
        }
        toast.success("Evaluation completed successfully.");
        return;
      }

      setJobId(data.job_id);
      const pollStartedAt = Date.now();
      setProgress((current) => Math.max(current, 10));
      setProgressLabel("Queued for hybrid control evaluation...");

      const pollJob = async () => {
        try {
          const { data: status } = await API.get(`/job-status/${data.job_id}`, withTimeout());

          if (status.state === "PENDING") {
            setProgress((current) => Math.max(current, 10));
            const pendingForMs = Date.now() - pollStartedAt;
            // Brief initial queue message only. Most evaluations start quickly;
            // don't blast a warning-sounding message unless it's been a while.
            if (pendingForMs < CONTROL_JOB_PENDING_WARNING_MS) {
              setProgressLabel("Queued — dispatching to evaluation worker...");
            } else {
              setProgressLabel(
                pendingForMs >= CONTROL_JOB_PENDING_WARNING_MS
                  ? "Evaluation queued — worker is picking up the job."
                  : "Queued for hybrid control evaluation..."
              );
            }
          } else if (status.state === "PROGRESS") {
            const step = status.meta?.step;

            if (step === "waiting_for_artifacts") {
              const pendingCount = Number(status.meta?.pending_count || 0);
              setProgress((current) => Math.max(current, 10));
              setProgressLabel(
                pendingCount > 0
                  ? `Waiting for ${pendingCount} artifact(s) to finish indexing...`
                  : "Waiting for uploaded artifacts to finish processing..."
              );
            } else if (step === "load_controls") {
              setProgress((current) => Math.max(current, 20));
              setProgressLabel("Loading Phase 3 controls and evidence stores...");
            } else if (step === "resolving_artifacts") {
              const currentIndex = Number(status.meta?.current_index || 0);
              const totalControls = Number(status.meta?.total_controls || 1);
              const nextProgress = Math.max(20, Math.min(28, Math.round(20 + (currentIndex / totalControls) * 8)));
              setProgress((current) => Math.max(current, nextProgress));
              setProgressLabel(
                status.meta?.current_control
                  ? `Matching ${status.meta.current_control} to uploaded artifacts...`
                  : "Matching controls to uploaded artifacts..."
              );
            } else if (step === "evaluate_sql_controls") {
              const currentIndex = Number(status.meta?.current_index || 0);
              const totalControls = Number(status.meta?.sql_candidate_controls || status.meta?.total_controls || 1);
              const nextProgress = Math.max(25, Math.min(90, Math.round(25 + (currentIndex / totalControls) * 55)));
              setProgress((current) => Math.max(current, nextProgress));
              setProgressLabel(
                status.meta?.current_control
                  ? `Evaluating ${status.meta.current_control} against CSV/OCR artifacts...`
                  : "Evaluating controls against CSV/OCR artifacts..."
              );
            } else if (step === "evaluate_llm_controls") {
              const currentIndex = Number(status.meta?.current_index || 0);
              const totalControls = Number(status.meta?.llm_candidate_controls || status.meta?.total_controls || 1);
              const nextProgress = Math.max(60, Math.min(95, Math.round(60 + (currentIndex / totalControls) * 25)));
              setProgress((current) => Math.max(current, nextProgress));
              setProgressLabel(
                status.meta?.current_control
                  ? `Grounding ${status.meta.current_control} in retrieved evidence...`
                  : "Validating  controls with grounded evidence..."
              );
            } else if (step === "evaluate_recon_controls") {
              const currentIndex = Number(status.meta?.current_index || 0);
              const totalControls = Number(status.meta?.total_controls || 1);
              const nextProgress = Math.max(88, Math.min(96, Math.round(88 + (currentIndex / totalControls) * 8)));
              setProgress((current) => Math.max(current, nextProgress));
              setProgressLabel(
                status.meta?.current_control
                  ? `Reconciling ${status.meta.current_control} across data sources...`
                  : "Running reconciliation checks across data sources..."
              );
            } else if (step === "finalize") {
              setProgress((current) => Math.max(current, 92));
              setProgressLabel("Saving hybrid evaluation findings...");
            }
          } else if (status.state === "SUCCESS") {
            setProgress(100);
            setProgressLabel("Evaluation complete.");
            setEvaluating(false);
            setJobId(null);

            const result = status.result || {};
            setLastEvalSummary(result);
            await Promise.all([fetchFindings(), fetchLatestEvaluationSummary(), fetchControls()]);
            if (setGraphCache) {
              setGraphCache({});
            }
            toast.success("Evaluation complete.");
            return;
          } else if (status.state === "FAILURE" || status.state === "REVOKED") {
            setEvaluating(false);
            setJobId(null);
            setProgress(0);
            setProgressLabel("Evaluation failed.");
            toast.error(status.error || "Control evaluation job failed.");
            return;
          }

          if (Date.now() - pollStartedAt >= CONTROL_JOB_MAX_DURATION_MS) {
            setEvaluating(false);
            setJobId(null);
            setProgress(0);
            setProgressLabel("Evaluation timed out.");
            toast.error("Control evaluation timed out. Please try again.");
            return;
          }

          setTimeout(pollJob, CONTROL_JOB_POLL_INTERVAL_MS);
        } catch (error) {
          console.error("Failed to poll control evaluation status:", error);
          setEvaluating(false);
          setJobId(null);
          setProgress(0);
          setProgressLabel("Failed to check evaluation status.");
          toast.error("Failed to check control evaluation status.");
        }
      };

      pollJob();
    } catch (error) {
      console.error("Failed to start control evaluation:", error);
      setEvaluating(false);
      setJobId(null);
      setProgress(0);
      setProgressLabel("Evaluation failed to start.");
      toast.error(renderEvaluationStartError(error));
    }
  };

  const handleContinueToTesting = async () => {
    navigate("/phase/7-testing");
  };

  const handleContinueToFindings = async () => {
    navigate("/phase/8");
  };

  const effectiveEvalSummary = (coerceEvaluationSummary(lastEvalSummary) || latestEvalSummary) || {};
  const inScopeControls = useMemo(() => controls.filter(c => c.in_scope), [controls]);

  const filteredControls = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return inScopeControls;
    return inScopeControls.filter((c) =>
      String(c.control_id || "").toLowerCase().includes(q) ||
      String(c.description || "").toLowerCase().includes(q) ||
      String(c.check_type || "").toLowerCase().includes(q)
    );
  }, [inScopeControls, searchQuery]);

  if (isTeamMode) {
    return (
      <>
        <Phase7TeamMode />
        {showSamplingPopup && <SamplingCompletePopup />}
      </>
    );
  }

  return (
    <PageLayout
      icon={RadioTower}
      title="Phase 7: Evidence & Control Testing"
      subtitle="Run automated hybrid diagnostics across in-scope controls against ingested data artifacts."
      className="phase7-console space-y-6 max-w-7xl mx-auto pb-12"
      data-testid="phase-7"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {auditRun?.name && (
            <Badge variant="outline" className="hidden sm:inline-flex bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 text-xs font-semibold px-3 py-1">
              {auditRun.name}
            </Badge>
          )}

          <Button
            onClick={handleEvaluate}
            disabled={evaluating || !auditRun}
            data-testid="evaluate-evidence-btn"
            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-9 px-4 shadow-md shadow-emerald-600/30 border-0 transition-all active:scale-95"
          >
            {evaluating ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Zap className="mr-1.5 h-3.5 w-3.5" />}
            {evaluating ? "Evaluating..." : "Start Auditing"}
          </Button>

          <Button
            onClick={handleContinueToTesting}
            disabled={findings.length === 0}
            data-testid="continue-to-testing-top-btn"
            variant="outline"
            className="rounded-xl text-xs font-bold h-9 px-3.5"
          >
            Control Testing <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Button>

          <Button
            onClick={handleContinueToFindings}
            disabled={findings.length === 0}
            data-testid="continue-to-findings-top-btn"
            variant="ghost"
            className="rounded-xl text-xs font-semibold h-9 px-2.5"
          >
            Findings
          </Button>
        </div>
      }
    >
      {showSamplingPopup && <SamplingCompletePopup />}

      {/* REAL-TIME PROGRESS BAR (WHEN EVALUATING) */}
      {evaluating && (
        <Card className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-2">
          <div className="flex items-center justify-between text-xs font-semibold">
            <span className="text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {progressLabel || "Evaluating controls..."}
            </span>
            <span className="text-emerald-700 dark:text-emerald-300 font-mono font-bold">{progress}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-emerald-500/20">
            <div className="h-full bg-emerald-600 transition-all duration-300 rounded-full" style={{ width: `${progress}%` }} />
          </div>
        </Card>
      )}

      {/* COMPACT STATS METRIC STRIP */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="rounded-2xl border border-border/80 bg-card p-4 hover:border-emerald-500/30 transition-all">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block">In-Scope Controls</span>
          <span className="text-2xl sm:text-3xl font-black text-foreground mt-1 block">{inScopeControls.length}</span>
        </Card>

        <Card className="rounded-2xl border border-border/80 bg-card p-4 hover:border-emerald-500/30 transition-all">
          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 block">Evaluated</span>
          <span className="text-2xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400 mt-1 block">{effectiveEvalSummary?.total_evaluated || 0}</span>
        </Card>

        <Card className="rounded-2xl border border-border/80 bg-card p-4 hover:border-emerald-500/30 transition-all">
          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 block">Passed</span>
          <span className="text-2xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400 mt-1 block">{effectiveEvalSummary?.passed || 0}</span>
        </Card>

        <Card className="rounded-2xl border border-border/80 bg-card p-4 hover:border-emerald-500/30 transition-all">
          <span className="text-[10px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400 block">Failed / Exceptions</span>
          <span className="text-2xl sm:text-3xl font-black text-red-600 dark:text-red-400 mt-1 block">{effectiveEvalSummary?.failed || 0}</span>
        </Card>
      </div>

      {/* TABBED DIAGNOSTIC WORKSPACE */}
      <Tabs defaultValue="matrix" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border/60 pb-3">
          <TabsList className="bg-muted/60 p-1 rounded-2xl border border-border/60">
            <TabsTrigger value="matrix" className="rounded-xl text-xs font-bold px-4 py-1.5">
              <Activity className="w-3.5 h-3.5 mr-1.5 text-emerald-500" />
              Control Readiness Matrix ({filteredControls.length})
            </TabsTrigger>
            <TabsTrigger value="graph" className="rounded-xl text-xs font-bold px-4 py-1.5">
              <Network className="w-3.5 h-3.5 mr-1.5 text-teal-500" />
              Evidence Knowledge Graph
            </TabsTrigger>
          </TabsList>

          {activeTab === "matrix" && (
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Filter controls..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 pl-8 pr-3 text-xs rounded-xl bg-card border-border/80 focus-visible:ring-emerald-500/30"
              />
            </div>
          )}
        </div>

        {/* TAB 1: CONTROL READINESS MATRIX */}
        <TabsContent value="matrix" className="space-y-4 m-0">
          <Card className="rounded-3xl border-border/80 shadow-sm overflow-hidden">
            <CardContent className="p-0">
              <div className="divide-y divide-border/60">
                {filteredControls.length > 0 ? (
                  filteredControls.map((ctrl) => {
                    const isTestable = ctrl.check_type === "SQL" || ctrl.check_type === "RAG";
                    const matchingFinding = findings.find(f => f.control_id === ctrl.id || f.control_code === ctrl.control_id);

                    return (
                      <div
                        key={ctrl.id}
                        className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-accent/40 transition-colors group"
                      >
                        <div className="space-y-1.5 min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-mono text-xs font-extrabold text-foreground group-hover:text-emerald-600 dark:group-hover:text-emerald-400">
                              {ctrl.control_id}
                            </span>
                            {ctrl.category && (
                              <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded-full font-medium">
                                {ctrl.category}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-foreground font-medium max-w-3xl leading-relaxed whitespace-pre-wrap break-words">
                            {ctrl.description}
                          </p>
                        </div>

                        <div className="flex items-center gap-3 shrink-0">
                          {matchingFinding && (
                            <Badge className="bg-red-500/10 text-red-600 border-red-500/30 dark:text-red-400 text-xs font-semibold px-2.5 py-1">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Finding Generated
                            </Badge>
                          )}
                          <span
                            className={`h-2.5 w-2.5 rounded-full shrink-0 ${isTestable ? 'bg-emerald-500 shadow-sm shadow-emerald-500/50' : 'bg-slate-400'}`}
                            title={isTestable ? "Automated test ready" : "Manual review"}
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-12 text-center text-xs text-muted-foreground space-y-2">
                    <FileText className="mx-auto h-8 w-8 text-muted-foreground/40" />
                    <p className="font-semibold text-foreground">No controls found</p>
                    <p>No in-scope controls match your search criteria.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* EXECUTION FAILURES (IF ANY) */}
          {effectiveEvalSummary?.failed_controls?.length > 0 && (
            <Card className="rounded-3xl border-amber-500/30 bg-amber-500/5 dark:bg-amber-950/20 shadow-sm overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-bold text-amber-700 dark:text-amber-300 flex items-center gap-2 uppercase tracking-wider">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Diagnostic Failures ({effectiveEvalSummary.failed_controls.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 p-4">
                {(effectiveEvalSummary.failed_controls || []).map((item, index) => (
                  <div key={index} className="rounded-2xl border border-amber-500/20 bg-card p-3 text-xs shadow-sm space-y-1">
                    <div className="font-mono text-xs text-amber-600 dark:text-amber-400 font-bold">{item.control_id || "Unknown Control"}</div>
                    <p className="text-muted-foreground">{toSafeControlError(item.message)}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* TAB 2: EVIDENCE KNOWLEDGE GRAPH */}
        <TabsContent value="graph" className="m-0">
          <Card className="rounded-3xl border-border/80 shadow-sm overflow-hidden">
            <CardContent className="p-4">
              {auditRun?.id ? (
                <EvidenceGraphPanel auditRunId={auditRun.id} view="all" className="h-[600px] rounded-2xl" />
              ) : (
                <div className="p-12 text-center text-xs text-muted-foreground">
                  Active audit run required to render knowledge graph.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* BOTTOM ACTION BAR */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-border/60">
        <p className="text-xs text-muted-foreground font-medium">
          Ready to review test execution results and control sample assertions.
        </p>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleContinueToFindings}
            data-testid="skip-to-findings-btn"
            className="rounded-xl text-xs font-semibold"
          >
            Skip to Findings
          </Button>
          <Button
            onClick={handleContinueToTesting}
            data-testid="continue-to-testing-btn"
            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md shadow-emerald-600/20"
          >
            Continue to Control Testing <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </div>
    </PageLayout>
  );
}

